/**
 * Social Authentication Service
 * 
 * Handles Google and Apple sign-in using @capacitor-firebase/authentication plugin
 * for native platforms and Firebase web SDK for web.
 */

import { Capacitor } from '@capacitor/core';
import {
    FirebaseAuthentication,
    SignInResult
} from '@capacitor-firebase/authentication';
import {
    GoogleAuthProvider,
    OAuthProvider,
    signInWithCredential,
    signInWithPopup,
    type UserCredential
} from 'firebase/auth';
import { auth } from './firebase';

/**
 * Sign in with Google
 * Uses native Google Sign-In on iOS/Android, popup on web
 */
export async function signInWithGoogle(): Promise<UserCredential> {
    if (Capacitor.isNativePlatform()) {
        // Native platform: use Capacitor plugin
        const result: SignInResult = await FirebaseAuthentication.signInWithGoogle();

        // Get the ID token from the result
        const idToken = result.credential?.idToken;
        if (!idToken) {
            throw new Error('No ID token received from Google Sign-In');
        }

        // Create Firebase credential and sign in
        const credential = GoogleAuthProvider.credential(idToken);
        return signInWithCredential(auth, credential);
    } else {
        // Web platform: use popup sign-in
        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        return signInWithPopup(auth, provider);
    }
}

/**
 * Sign in with Apple
 * Uses native Apple Sign-In on iOS/Android, popup on web
 */
export async function signInWithApple(): Promise<UserCredential> {
    if (Capacitor.isNativePlatform()) {
        // Native platform: use Capacitor plugin
        const result: SignInResult = await FirebaseAuthentication.signInWithApple({
            skipNativeAuth: false
        });

        // Get the ID token and nonce from the result
        const idToken = result.credential?.idToken;
        const rawNonce = result.credential?.nonce;

        if (!idToken) {
            throw new Error('No ID token received from Apple Sign-In');
        }

        // Create Firebase credential and sign in
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
            idToken,
            rawNonce
        });
        return signInWithCredential(auth, credential);
    } else {
        // Web platform: use popup sign-in
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        return signInWithPopup(auth, provider);
    }
}

/**
 * Check if Apple Sign-In is available on this device
 * (iOS 13+ or macOS Catalina+, or web with Apple provider configured)
 */
export function isAppleSignInAvailable(): boolean {
    // On native iOS, Apple Sign-In should be available on iOS 13+
    // On web, it's available if Apple is configured as a provider in Firebase
    // On Android, Apple Sign-In is available but less common
    return true; // Always show the option, Firebase will handle availability
}
